import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";
import { cn } from "./ui/cn";

/**
 * Follow a ticket you do not own.
 *
 * The point of it is the ticket that is not yours and not in any of your views:
 * a colleague's escalation you handed over, a customer you know. Without this
 * the only way to keep an eye on one is to remember to go and look.
 *
 * Watching is explicit and never inferred -- being the assignee does not make
 * you a watcher, so the count here is the whole truth about who else is
 * following this conversation. The assignee hears about their own ticket
 * through their own switches.
 */
export function WatchButton({ ticketId }: { ticketId: number }) {
  const queryClient = useQueryClient();

  const watchers = useQuery({
    queryKey: queryKeys.ticketWatchers(ticketId),
    queryFn: () => api.ticketWatchers(ticketId),
    staleTime: 60_000,
  });

  const toggle = useMutation({
    mutationFn: (watching: boolean) =>
      api.setTicketWatching(ticketId, watching),
    onSuccess: (data) =>
      queryClient.setQueryData(queryKeys.ticketWatchers(ticketId), data),
  });

  /* Nothing at all until it is known. A button that says "Watch" and then
     flips to "Watching" on load is a button that looked wrong for a moment. */
  if (!watchers.data) return null;

  const { watching, watchers: people } = watchers.data;
  const others = watching ? people.length - 1 : people.length;

  return (
    <button
      type="button"
      onClick={() => toggle.mutate(!watching)}
      disabled={toggle.isPending}
      aria-pressed={watching}
      title={
        others > 0
          ? `${people.map((person) => person.name).join(", ")}`
          : undefined
      }
      className={cn(
        "rounded-md border px-2 py-1 text-xs transition-colors disabled:opacity-60",
        watching
          ? "border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300"
          : "border-line text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
      )}
    >
      {watching ? "Watching" : "Watch"}
      {others > 0 ? (
        <span className="ml-1 text-faint">
          {watching ? `+${others}` : others}
        </span>
      ) : null}
    </button>
  );
}
